import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useFilterMachineFixings } from "../../hooks/useFilterMachineFixings";
import { useFilterTiltrotators } from "../../hooks/useFilterTiltrotators";
import { useScrollToProduct } from "../../hooks/useScrollToProduct";
import { GrappleModule } from "../../interfaces/api/GrappleModule";
import { MachineFixing } from "../../interfaces/api/MachineFixing";
import { Rototilt } from "../../interfaces/api/Rototilt";
import { MachineFixingType } from "../../shared/enums/MachineFixingType";
import { ProductCategory } from "../../shared/enums/ProductCategory";
import { ProductType } from "../../shared/enums/ProductType";
import { ListObject } from "../../shared/types/ListObject";
import { ProductItem } from "../../shared/types/ProductItem";
import { action } from "../../store/action";
import { useProductByType } from "../../store/hooks/useProductByType";
import { ProductData } from "../../store/interfaces/productData";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import MachineFixingsOptional from "../MachineFixings/MachineFixingsOptional";
import { useFilterGrappleModules } from "../Products/hooks/useFilterGrappleModules";
import { useStateCleaner } from "../Products/hooks/useStateCleaner";
import TiltrotatorsOptional from "../Tiltrotator/TiltrotatorsOptional";
import ToolsOptional from "../Tools/ToolsOptional";
import "./Optional.scss";

interface OptionalProps {
  isMachineFixingsSelected: boolean;
  isNoneGrappleModuleSelected: boolean;
  isToolsSelected: boolean;
  isTiltrotatorsSelected: boolean;
  productCategory: ProductCategory;
  selectedGrappleModule: GrappleModule;
  selectedMachineFixing: MachineFixing;
  selectedMachineFixingType: MachineFixingType;
  selectedTiltrotator: Rototilt;
  selectedToolsProductItems: ProductItem[];
  selectedToolsProductTypes: ProductType[];
}

enum ConfirmMode {
  None = -1,
  TiltrotatorAccordian = 0,
  Tiltrotator = 1,
  MachineFixingsAccoridan = 2,
  MachineFixingType = 3,
  ToolsAccordian = 4,
}

const Optional = ({
  isMachineFixingsSelected = false,
  isNoneGrappleModuleSelected,
  isToolsSelected,
  isTiltrotatorsSelected,
  productCategory,
  selectedGrappleModule,
  selectedMachineFixing,
  selectedMachineFixingType,
  selectedTiltrotator,
  selectedToolsProductItems = [],
  selectedToolsProductTypes = [],
}: OptionalProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("popup");
  const {
    clearStateForOptionalMachineFixingsAccordian,
    clearStateForOptionalTiltrotator,
    clearStateForOptionalTiltrotatorAccordian,
    clearStateForOptionalToolsAccordian,
  } = useStateCleaner();
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [confirmMode, setConfirmMode] = useState<ConfirmMode>(ConfirmMode.None);
  const [oldTiltrotator, setOldTiltrotator] = useState<Rototilt>(undefined);
  const [oldMachineFixingType, setOldMachineFixingType] =
    useState<MachineFixingType>(undefined);
  const [oldToolsProductItems, setOldToolsProductItems] = useState<
    ProductItem[]
  >([]);
  const [oldToolsProductTypes, setOldToolsProductTypes] = useState<
    ProductType[]
  >([]);

  const {
    refGrappleModules,
    refToolsItems,
    refToolsProductItemsList,
    scrollToGrappleModules,
    scrollToToolsProductItemsList,
  } = useScrollToProduct();
  const tiltrotatorsData = useProductByType(
    ProductType.Rototilts
  ) as ProductData;
  const machineFixingsData = useProductByType(
    ProductType.MachineFixings
  ) as ProductData;
  const grappleModulesData = useProductByType(
    ProductType.GrappleModules
  ) as ProductData;
  const { filteredTiltrotators } = useFilterTiltrotators({
    tiltrotatorsData,
  });
  const { filteredMachineFixings } = useFilterMachineFixings({
    machineFixingsData,
    selectedProductCategory: productCategory,
    selectedTiltrotator: undefined,
  });

  const { filteredGrappleModules } = useFilterGrappleModules({
    grappleModulesData,
    lowerBracket: undefined,
    tiltrotator: selectedTiltrotator,
  });

  const onToolsAccordianClick = () => {
    const newValue = !isToolsSelected;
    setOldToolsProductTypes(selectedToolsProductTypes);
    setOldToolsProductItems(selectedToolsProductItems);
    dispatch(action.saveOptionalToolsIsToolsSelected(newValue));

    if (!newValue) {
      if (
        (selectedToolsProductTypes && selectedToolsProductTypes.length > 0) ||
        (selectedToolsProductItems && selectedToolsProductItems.length > 0)
      ) {
        setConfirmMode(ConfirmMode.ToolsAccordian);
        setShowConfirmDialog(true);
        return;
      }
      clearStateForOptionalToolsAccordian();
    }
  };

  const onTiltrotatosAccordianClick = () => {
    const newValue = !isTiltrotatorsSelected;
    dispatch(action.saveOptionalToolsIsTiltrotatorsSelected(newValue));

    if (!newValue) {
      if (selectedTiltrotator) {
        setConfirmMode(ConfirmMode.TiltrotatorAccordian);
        setShowConfirmDialog(true);
        return;
      }
      clearStateForOptionalTiltrotatorAccordian();
    }
  };

  const onMachineFixingsAccordianClick = () => {
    const newValue = !isMachineFixingsSelected;
    dispatch(action.saveOptionalToolsIsMachineFixingsSelected(newValue));

    if (!newValue) {
      if (selectedMachineFixingType) {
        setConfirmMode(ConfirmMode.MachineFixingsAccoridan);
        setShowConfirmDialog(true);
        return;
      }
      clearStateForOptionalMachineFixingsAccordian();
    }
  };

  const onToolsProductTypeChange = (productType: ProductType) => {
    const index = selectedToolsProductTypes.indexOf(productType);

    if (index > -1) {
      const filteredSelectedToolsProductTypes =
        selectedToolsProductTypes.filter(
          (selectedProductType) => selectedProductType !== productType
        );

      dispatch(
        action.saveOptionalToolsProductTypes([
          ...filteredSelectedToolsProductTypes,
        ])
      );

      return;
    }

    dispatch(
      action.saveOptionalToolsProductTypes([
        ...selectedToolsProductTypes,
        productType,
      ])
    );

    scrollToToolsProductItemsList();
  };

  const onToolsListObjectChange = (productItem: ListObject) => {
    const index = selectedToolsProductItems.findIndex(
      (selectedToolsProductItem) =>
        selectedToolsProductItem.id === productItem.id
    );

    if (index > -1) {
      const filteredSelectedToolsProductItems =
        selectedToolsProductItems.filter(
          (selectedToolsProductItem) =>
            selectedToolsProductItem.id !== productItem.id
        );

      dispatch(
        action.saveCartSelectionsOptionalToolsProductItems([
          ...filteredSelectedToolsProductItems,
        ])
      );

      return;
    }

    dispatch(
      action.saveCartSelectionsOptionalToolsProductItems([
        ...selectedToolsProductItems,
        productItem as ProductItem,
      ])
    );
  };

  const onTiltrotatorChange = (tiltrotator: Rototilt) => {
    setOldTiltrotator(selectedTiltrotator);
    dispatch(action.saveCartSelectionsOptionalTiltrotator(tiltrotator));

    if (selectedGrappleModule || isNoneGrappleModuleSelected) {
      setConfirmMode(ConfirmMode.Tiltrotator);
      setShowConfirmDialog(true);
      return;
    }

    clearStateForOptionalTiltrotator();
    scrollToGrappleModules();
  };

  const onMachineFixingTypeChange = (machineFixingType: MachineFixingType) => {
    setOldMachineFixingType(selectedMachineFixingType);
    dispatch(action.saveOptionalMachineFixingType(machineFixingType));

    if (selectedMachineFixing) {
      setConfirmMode(ConfirmMode.MachineFixingType);
      setShowConfirmDialog(true);
      return;
    }

    dispatch(action.saveCartSelectionsOptionalMachineFixing(undefined));
  };

  const onMachineFixingChange = (machineFixing: MachineFixing) => {
    dispatch(action.saveCartSelectionsOptionalMachineFixing(machineFixing));
  };

  const onGrappleModuleChange = (grappleModule: GrappleModule) => {
    dispatch(action.saveCartSelectionsOptionalGrappleModule(grappleModule));
    dispatch(action.saveCartSelectionsOptionalNoGrappleModuleSelected(false));
  };

  const onNoneGrapppleModuleChange = () => {
    dispatch(action.saveCartSelectionsOptionalGrappleModule(undefined));
    dispatch(action.saveCartSelectionsOptionalNoGrappleModuleSelected(true));
  };

  const onDialogOk = () => {
    switch (confirmMode) {
      case ConfirmMode.TiltrotatorAccordian:
        clearStateForOptionalTiltrotatorAccordian();
        break;
      case ConfirmMode.Tiltrotator:
        clearStateForOptionalTiltrotator();
        scrollToGrappleModules();
        break;
      case ConfirmMode.MachineFixingsAccoridan:
        clearStateForOptionalMachineFixingsAccordian();
        break;
      case ConfirmMode.MachineFixingType:
        dispatch(action.saveCartSelectionsOptionalMachineFixing(undefined));
        break;
      case ConfirmMode.ToolsAccordian:
        clearStateForOptionalToolsAccordian();
        break;
    }

    setShowConfirmDialog(false);
  };

  const onDialogClose = () => {
    switch (confirmMode) {
      case ConfirmMode.TiltrotatorAccordian:
        dispatch(action.saveOptionalToolsIsTiltrotatorsSelected(true));
        break;
      case ConfirmMode.Tiltrotator:
        dispatch(action.saveCartSelectionsOptionalTiltrotator(oldTiltrotator));
        break;
      case ConfirmMode.MachineFixingsAccoridan:
        dispatch(action.saveOptionalToolsIsMachineFixingsSelected(true));
        break;
      case ConfirmMode.MachineFixingType:
        dispatch(action.saveOptionalMachineFixingType(oldMachineFixingType));
        break;
      case ConfirmMode.ToolsAccordian:
        dispatch(action.saveOptionalToolsIsToolsSelected(true));
        dispatch(
          action.saveOptionalToolsProductTypes([...oldToolsProductTypes])
        );
        dispatch(
          action.saveCartSelectionsOptionalToolsProductItems([
            ...oldToolsProductItems,
          ])
        );
        break;
    }

    setShowConfirmDialog(false);
  };

  return (
    <div className="optional">
      {productCategory === ProductCategory.Tiltrotator && (
        <div className="optionalContainer">
          <ToolsOptional
            isAccordianExpanded={isToolsSelected}
            onAccordianClick={onToolsAccordianClick}
            onListObjectChange={onToolsListObjectChange}
            onProductTypeChange={onToolsProductTypeChange}
            refToolsItems={refToolsItems}
            refToolsProductItemsList={refToolsProductItemsList}
            selectedToolsProductItems={selectedToolsProductItems}
            selectedToolsProductTypes={selectedToolsProductTypes}
          />
        </div>
      )}
      {productCategory === ProductCategory.MachineFixings && (
        <>
          <div className="optionalContainer">
            <TiltrotatorsOptional
              grappleModules={filteredGrappleModules}
              isAccordianExpanded={isTiltrotatorsSelected}
              isGrappleModuleNoneSelected={isNoneGrappleModuleSelected}
              onAccordianClick={onTiltrotatosAccordianClick}
              onGrappleModuleChange={onGrappleModuleChange}
              onGrappleModuleNoneChange={onNoneGrapppleModuleChange}
              onTiltrotatorChange={onTiltrotatorChange}
              refGrappleModules={refGrappleModules}
              selectedGrappleModule={selectedGrappleModule}
              selectedTiltrotator={selectedTiltrotator}
              tiltrotators={filteredTiltrotators}
            />
          </div>
          <div className="optionalContainer">
            <ToolsOptional
              isAccordianExpanded={isToolsSelected}
              onAccordianClick={onToolsAccordianClick}
              onListObjectChange={onToolsListObjectChange}
              onProductTypeChange={onToolsProductTypeChange}
              refToolsItems={refToolsItems}
              refToolsProductItemsList={refToolsProductItemsList}
              selectedToolsProductItems={selectedToolsProductItems}
              selectedToolsProductTypes={selectedToolsProductTypes}
            />
          </div>
        </>
      )}
      {productCategory === ProductCategory.Tools && (
        <>
          <div className="optionalContainer">
            <TiltrotatorsOptional
              grappleModules={filteredGrappleModules}
              isAccordianExpanded={isTiltrotatorsSelected}
              isGrappleModuleNoneSelected={isNoneGrappleModuleSelected}
              onAccordianClick={onTiltrotatosAccordianClick}
              onGrappleModuleChange={onGrappleModuleChange}
              onGrappleModuleNoneChange={onNoneGrapppleModuleChange}
              onTiltrotatorChange={onTiltrotatorChange}
              refGrappleModules={refGrappleModules}
              selectedGrappleModule={selectedGrappleModule}
              selectedTiltrotator={selectedTiltrotator}
              tiltrotators={filteredTiltrotators}
            />
          </div>
          <div className="optionalContainer">
            <MachineFixingsOptional
              isAccordianExpanded={isMachineFixingsSelected}
              machineFixnings={filteredMachineFixings}
              onAccordianClick={onMachineFixingsAccordianClick}
              onMachineFixingChange={onMachineFixingChange}
              onMachineFixingTypeChange={onMachineFixingTypeChange}
              productCategory={productCategory}
              selectedMachineFixing={selectedMachineFixing}
              selectedMachineFixingType={selectedMachineFixingType}
              selectedTiltrotator ={selectedTiltrotator}
            />
          </div>
        </>
      )}
      <ConfirmDialog
        dialogHeader={t("important")}
        text={t("areyousure")}
        okButtonText={t("clearchoises")}
        cancelButtonText={t("cancel")}
        open={showConfirmDialog}
        onClose={onDialogClose}
        onCancelClick={onDialogClose}
        onOkClick={onDialogOk}
      ></ConfirmDialog>
    </div>
  );
};

export default Optional;
